import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wifi,
  Car,
  Clock,
  LogOut as CheckOutIcon,
  ScrollText,
  MapPin,
  Coffee,
  Compass,
  Star,
  Check,
} from "lucide-react";
import UNSPLASH from "../unsplashImages";

/* ─── Dummy data ─── */

const DUMMY_RESTAURANTS = [
  { name: "Coastal Table", cat: "Seafood", dist: "8 min", vibe: "Date Night", img: UNSPLASH.thumbs.restaurant },
  { name: "La Terraza", cat: "Italian", dist: "5 min", vibe: "Family Friendly", img: UNSPLASH.thumbs.restaurant2 },
];
const DUMMY_COFFEE = [
  { name: "Morning Tide", cat: "Coffee", dist: "5 min", vibe: "Walkable", img: UNSPLASH.thumbs.coffee },
  { name: "Brew & Co.", cat: "Espresso", dist: "3 min", vibe: "Cozy", img: UNSPLASH.thumbs.coffee2 },
];
const DUMMY_ACTIVITIES = [
  { name: "Sunset Point Trail", cat: "Outdoors", dist: "12 min", vibe: "Scenic", img: UNSPLASH.thumbs.trail },
  { name: "Harbor Market", cat: "Shopping", dist: "6 min", vibe: "Local Fave", img: UNSPLASH.thumbs.market },
];

const PROPERTY_FIELDS = {
  wifi: { label: "Wi-Fi", value: "Bellhopt Guest", icon: "📶" },
  checkin: { label: "Check-In", value: "4:00 PM", icon: "🔑" },
  checkout: { label: "Check-Out", value: "10:00 AM", icon: "🚪" },
  parking: { label: "Parking", value: "2 cars, driveway", icon: "🅿️" },
  rules: { label: "Rules", value: "No smoking, quiet 10 PM", icon: "📋" },
};

/* ─── Toggle groups: only Local Guide + Property Essentials ─── */

const TOGGLE_GROUPS = [
  {
    label: "Local Guide",
    items: [
      { key: "restaurants", label: "Restaurants", icon: MapPin, defaultOn: true },
      { key: "activities", label: "Things To Do", icon: Compass, defaultOn: true },
      { key: "coffee", label: "Coffee Shops", icon: Coffee, defaultOn: true },
    ],
  },
  {
    label: "Property Essentials",
    items: [
      { key: "wifi", label: "Wi-Fi", icon: Wifi, defaultOn: true },
      { key: "checkin", label: "Check-In Info", icon: Clock, defaultOn: true },
      { key: "checkout", label: "Check-Out Info", icon: CheckOutIcon, defaultOn: true },
      { key: "parking", label: "Parking", icon: Car, defaultOn: true },
      { key: "rules", label: "House Rules", icon: ScrollText, defaultOn: true },
    ],
  },
];

function buildDefaults() {
  const s = {};
  TOGGLE_GROUPS.forEach((g) => g.items.forEach((i) => { s[i.key] = i.defaultOn; }));
  s.concierge = true;
  return s;
}

/* ─── Phone design tokens (matches PhoneDemo.js) ─── */
const DS = {
  bg: "#ffffff", card: "#F7F7F8", border: "#EAEAEA",
  text: "#111111", sub: "#5F6368", muted: "#888888",
  accent: "#EF4949", emerald: "#10b981", divider: "rgba(0,0,0,0.06)",
};
const pBase = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  WebkitFontSmoothing: "antialiased", boxSizing: "border-box",
};

/* ─── Main export ─── */

export default function GuidebookPreview({ hostLogo, customServices = [] }) {
  const [toggles, setToggles] = useState(buildDefaults);
  const handleToggle = (key) => setToggles((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="flex flex-col laptop:flex-row gap-8 laptop:gap-12 items-start">
      {/* Left: Copy + toggles */}
      <div className="flex-1 min-w-0 laptop:max-w-[380px]">
        <div className="flex flex-wrap gap-2 mb-4">
          {["Branded", "Guest-Ready", "Concierge-Connected"].map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm">
              <Star size={10} className="text-primary-400" />
              {tag}
            </span>
          ))}
        </div>

        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          Preview how Bellhopt turns your home details into a branded guest
          guidebook — local favorites, property essentials, and concierge
          access in one place.
        </p>

        <div className="space-y-4">
          {TOGGLE_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-1.5">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const on = toggles[item.key];
                  return (
                    <div
                      key={item.key}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all ${
                        on ? "bg-white border-primary-200 shadow-sm" : "bg-white/60 border-gray-100 hover:border-gray-200"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${on ? "bg-primary-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                        <item.icon size={13} strokeWidth={2} />
                      </div>
                      <span className={`flex-1 text-[13px] font-medium ${on ? "text-gray-900" : "text-gray-500"}`}>
                        {item.label}
                      </span>
                      <button
                        onClick={() => handleToggle(item.key)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? "bg-primary-500" : "bg-gray-300"}`}
                      >
                        <motion.span
                          className="inline-block h-4 w-4 rounded-full bg-white shadow-sm"
                          animate={{ x: on ? 17 : 2 }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
            <Check size={11} strokeWidth={3} />
            Included with Concierge subscription
          </span>
        </div>
      </div>

      {/* Right: iPhone mockup */}
      <div className="hidden laptop:flex flex-1 items-start justify-center">
        <GuidebookPhone toggles={toggles} hostLogo={hostLogo} customServices={customServices} />
      </div>
    </div>
  );
}

/* ─── iPhone mockup ─── */

function GuidebookPhone({ toggles, hostLogo, customServices = [] }) {
  const hasLocal = toggles.restaurants || toggles.activities || toggles.coffee;
  const hasProperty = toggles.wifi || toggles.checkin || toggles.checkout || toggles.parking || toggles.rules;
  const activeCustom = customServices.filter((s) => s.hostService?.enabled || s.name);

  return (
    <motion.div
      animate={{ y: [0, -7, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      style={{
        width: 260, height: 530, borderRadius: 42, background: "#000",
        border: "1px solid rgba(0,0,0,0.08)", position: "relative",
        boxShadow: "0 30px 80px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)",
        overflow: "hidden", flexShrink: 0,
      }}
    >
      <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: 72, height: 22, borderRadius: 11, background: "#000", zIndex: 20 }} />
      <div style={{ position: "absolute", top: 5, left: 5, right: 5, bottom: 5, borderRadius: 37, overflow: "hidden", background: DS.bg }}>
        <div style={{ ...pBase, width: "100%", height: "100%", overflowY: "auto", overflowX: "hidden", msOverflowStyle: "none", scrollbarWidth: "none" }} className="no-scrollbar">
          <div style={{ height: 48 }} />

          {/* Header */}
          <div style={{ padding: "0 20px 10px", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 40, marginBottom: 6 }}>
              {hostLogo ? (
                <img src={hostLogo} alt="" style={{ maxHeight: 38, maxWidth: 140, objectFit: "contain", borderRadius: 4 }} />
              ) : (
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: DS.muted }}>BELLHOPT</span>
              )}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3, color: DS.text }}>Guidebook</div>
            <div style={{ fontSize: 11, color: DS.sub, marginTop: 2 }}>Jun 15 – Jun 20, 2026</div>
          </div>

          <div style={{ height: 1, background: DS.divider, margin: "4px 20px 12px" }} />

          <div style={{ padding: "0 16px" }}>
            <AnimatePresence mode="popLayout">
              {/* LOCAL FAVORITES */}
              {hasLocal && (
                <motion.div key="local-hdr" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <SectionLabel>Local Favorites</SectionLabel>
                </motion.div>
              )}
              {toggles.restaurants && DUMMY_RESTAURANTS.map((r) => <RecoCard key={r.name} item={r} />)}
              {toggles.activities && DUMMY_ACTIVITIES.map((r) => <RecoCard key={r.name} item={r} />)}
              {toggles.coffee && DUMMY_COFFEE.map((r) => <RecoCard key={r.name} item={r} />)}

              {/* PROPERTY ESSENTIALS */}
              {hasProperty && (
                <motion.div key="prop-hdr" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ marginTop: 10 }}>
                  <SectionLabel>Home Essentials</SectionLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {toggles.wifi && <MiniCard {...PROPERTY_FIELDS.wifi} />}
                    {toggles.checkin && <MiniCard {...PROPERTY_FIELDS.checkin} />}
                    {toggles.checkout && <MiniCard {...PROPERTY_FIELDS.checkout} />}
                    {toggles.parking && <MiniCard {...PROPERTY_FIELDS.parking} />}
                    {toggles.rules && <MiniCard {...PROPERTY_FIELDS.rules} />}
                  </div>
                </motion.div>
              )}

              {/* CUSTOM SERVICES — host-created */}
              {activeCustom.length > 0 && (
                <motion.div key="custom-hdr" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ marginTop: 10 }}>
                  <SectionLabel>Your Services</SectionLabel>
                  {activeCustom.map((svc) => (
                    <motion.div
                      key={svc.key}
                      layout
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      style={{
                        ...pBase, display: "flex", alignItems: "center", gap: 8,
                        background: DS.card, border: `1px solid ${DS.border}`, borderRadius: 12,
                        padding: "9px 12px", marginBottom: 6,
                      }}
                    >
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(239,73,73,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 12 }}>✦</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: DS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{svc.name}</div>
                        {svc.hostService?.pricing?.flatUSD != null && (
                          <div style={{ fontSize: 9, color: DS.muted, marginTop: 1 }}>${svc.hostService.pricing.flatUSD}</div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {/* CONCIERGE CTA — always on */}
              {toggles.concierge && (
                <motion.div key="concierge" layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
                  style={{ ...pBase, background: DS.accent, borderRadius: 14, padding: "12px 14px", marginTop: 8 }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 3 }}>Need Anything?</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.8)", lineHeight: 1.3 }}>Your concierge is one tap away.</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div style={{ height: 20 }} />
        </div>
        <div style={{ position: "absolute", bottom: 7, left: "50%", transform: "translateX(-50%)", width: 110, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.12)", zIndex: 15 }} />
      </div>
    </motion.div>
  );
}

/* ─── Section label inside phone ─── */
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: DS.text, marginBottom: 8, letterSpacing: -0.2 }}>
      {children}
    </div>
  );
}

/* ─── Recommendation card ─── */
function RecoCard({ item }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      style={{
        ...pBase, display: "flex", alignItems: "center", gap: 10,
        background: DS.card, border: `1px solid ${DS.border}`, borderRadius: 12,
        padding: "7px 10px", marginBottom: 6, overflow: "hidden",
      }}
    >
      <img
        src={item.img} alt=""
        style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
        loading="lazy"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: DS.text,
          overflow: "hidden", display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          lineHeight: 1.3,
        }}>{item.name}</div>
        <div style={{ fontSize: 10, color: DS.muted, marginTop: 1 }}>{item.cat} · {item.dist}</div>
      </div>
      <div style={{
        padding: "3px 7px", borderRadius: 6, flexShrink: 0, maxWidth: 72,
        background: "rgba(239,73,73,0.08)", border: "1px solid rgba(239,73,73,0.1)",
        fontSize: 9, fontWeight: 600, color: DS.accent,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {item.vibe}
      </div>
    </motion.div>
  );
}

/* ─── Compact mini card for property fields ─── */
function MiniCard({ icon, label, value }) {
  return (
    <div style={{
      ...pBase, background: DS.card, border: `1px solid ${DS.border}`, borderRadius: 10,
      padding: "7px 9px", overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
        <span style={{ fontSize: 11 }}>{icon}</span>
        <span style={{ fontSize: 9, fontWeight: 600, color: DS.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: DS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );
}
